<template>
  <!-- Row: Chart -->
  <div
    class="row f-row"
    :style="{
      height: !isAdvanced
        ? '100%'
        : controlsSpace
        ? 'calc(100% - 36px)!important'
        : 'calc(100% - 22px)!important',
    }"
  >
    <div class="col col-12 text-center progress-div" v-if="showLoading">
      <QSLoading :isLoading="showLoading" :size="35" :colorTheme="colorTheme" />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="chartData.length === 0"
      style="position: absolute; left: 0; top: 47.5%;"
    >
      {{ zeroRequests ? "Get data" : "No Data Available" }}
    </div>
    <div class="col col-12" style="height:100%; width:100%">
      <div
        class="h-100 d-flex flex-column"
        id="planning-div"
        style="width: 100%"
      >
        <svg id="svg-planning"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorHedge,
  colorHedgeAlpha2,
  colorExpo,
  colorDarkExpo,
  colorFwd,
  colorFwdAlpha4,
  colorNet_2,
  colorNetAlpha_2,
  colorProgram,
  colorSymmetryAlpha3,
  currencyFormat,
  darkFwdF,
  darkFwdAlpha4,
  formatM,
  formatNumber,
  formatPercent,
  formatPercentSm,
  formatPercentXSm,
  numberFormat,
  textactive,
  textactive2,
  whiteGray,
  whiteGrayAlpha,
} from "@/_core/_helpers/_parametric_tools";
import { format, select } from "d3";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "PlanningChart",
  props: {
    asset: { required: false, default: "currencies" },
    averages: { required: true, default: () => [] },
    budgetRate: { required: true },
    chartData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    controlsSpace: { required: false, type: Boolean, default: true },
    currency: { required: true, default: () => [] },
    dateFormat: { required: true },
    expandFRow: { required: true },
    isAdvanced: { required: false, default: true },
    isFuture: { required: true },
    normalizePlanning: { required: true },
    processData: { required: true },
    renderChartSignal: { required: true },
    showBudget: { required: false, type: Boolean, default: false },
    showBasisRatio: { required: true },
    showExpo: { required: true },
    showFixedHedged: { required: false, type: Boolean, default: false },
    showHedge: { required: true },
    showLoading: { required: true },
    showProgram: { required: true },
    showProtected: { required: false, type: Boolean, default: false },
    showRequired: { required: true },
    showTooltip: { required: true },
    zeroRequests: { required: true, type: Boolean, default: true },
  },
  components: { QSLoading },
  data() {
    return {
      currencyFormat: format(currencyFormat),
      D3Chart: null,
      D3HoverPlanning: null,
      fNames: [],
      fNamesNorm: [],
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
        isPrices: false,
        minMaxYAxis: [Infinity, -Infinity],
        svgNode: null,
      },
      logo: require("@/assets/images/symbol_blue.svg"),
    };
  },
  created() {
    this.D3Chart = new D3Chart("planning-div", "svg-planning");
    this.D3HoverPlanning = new D3HoverPlanning();
    this.fNames = this.isAdvanced
      ? {
          budget: "budget_expo",
          comm: "balance",
          expo: "cash_flow",
          fixed: "fixed",
          hedge: "hedge",
          program: "program",
          protected: "protected",
        }
      : {
          budget: "q_budget",
          comm: "q_commited",
          expo: "q_forecast",
          fixed: "q_fixed",
          hedge: "q_fixedHedged",
          program: "q_program",
          protected: "q_hedged",
        };
    this.fNamesNorm = this.isAdvanced
      ? {
          comm: "balanceP",
          hedge: "hedgeP",
          fixed: "fixedP",
          protected: "protectedP",
        }
      : {
          comm: "q_commitedPerc",
          hedge: "q_fixedHedgedPerc",
          fixed: "q_fixedPerc",
          protected: "q_hedgedPerc",
        };
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    showLoading(val) {
      if (this.chartData.length > 0 && !val) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    chartData() {
      if (this.chartData.length > 0 && !this.showLoading) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    renderChartSignal() {
      if (this.chartData.length > 0) {
        this.createBarChart();
      } else {
        this.deleteChart();
      }
    },
    showBudget() {
      this.callWidth();
    },
    showExpo() {
      this.callWidth();
    },
    showBasisRatio() {
      this.callWidth();
    },
    showHedge() {
      this.callWidth();
    },
    showProtected() {
      this.callWidth();
    },
    showRequired() {
      this.callWidth();
    },
    showProgram() {
      this.callWidth();
    },
    showTooltip() {
      this.callWidth();
    },
    normalizePlanning() {
      this.callWidth();
    },
    formatVars: {
      handler() {
        setTimeout(() => {
          this.onHoverChart();
        });
      },
      deep: true,
    },
  },
  methods: {
    deleteChart() {
      select("#planning-div")
        .selectAll(".tooltip")
        .remove();
      select("#planning-div")
        .select("svg > *")
        .remove();
    },
    validateMinMaxYAxis(minMaxYAxis) {
      let minMaxY = minMaxYAxis;
      if (minMaxY[0] === minMaxY[1]) {
        minMaxY = [0, minMaxY[0] * 2];
      } else if ((minMaxY[0] > 0) & (minMaxY[1] > 0)) {
        minMaxY[0] = 0;
      } else if ((minMaxY[0] < 0) & (minMaxY[1] < 0)) {
        minMaxY[1] = 0;
      }
      const del = 0.05 * (minMaxY[1] - minMaxY[0]);
      const del1 = minMaxY[0] === 0 ? 0 : del;
      const del2 = minMaxY[1] === 0 ? 0 : del;
      minMaxY = [
        Math.min(0, minMaxY[0] - del1),
        Math.max(0, minMaxY[1] + del2),
      ];

      return minMaxY;
    },
    createBarChart() {
      if (this.chartData.length === 0) {
        return;
      }
      let _this = this;
      this.minMaxYAxis = [Infinity, -Infinity];
      // this.chartData.forEach(function(d) {
      //   d.tot = d[_this.fNames["expo"]];
      // });
      // if (this.normalizePlanning) {
      this.chartData.forEach(function(d) {
        d.balanceN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["comm"]] / d[_this.fNames["expo"]];
        d.budgetN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["budget"]] / d[_this.fNames["expo"]];
        d.cash_flowN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["expo"]] / d[_this.fNames["expo"]];
        d.hedgeN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["hedge"]] / d[_this.fNames["expo"]];
        d.fixedN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["fixed"]] / d[_this.fNames["expo"]];
        d.protectedN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["protected"]] / d[_this.fNames["expo"]];
        d.last_m_hedgeN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d.last_m_hedge / d[_this.fNames["expo"]];
        d.programN =
          d[_this.fNames["expo"]] === 0
            ? 0
            : d[_this.fNames["program"]] / d[_this.fNames["expo"]];
        d.cash_flowN = 1;
      });

      // }
      // === set SVG canvas
      this.svgNode = this.D3Chart.setSVG(
        0.5,
        5,
        0.5,
        55,
        0.5,
        20,
        0.5,
        1,
        this.colorTheme,
        this.logo,
        "up",
      );
      if (this.svgNode.height === 0 || this.svgNode.width === 0) {
        return;
      }
      if (this.normalizePlanning) {
        if (this.showExpo) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            "cash_flowN",
            "cash_flowN",
          );
        }
        if (this.showBudget) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            "budgetN",
            "budgetN",
            this.minMaxYAxis,
          );
        }
        if (this.showBasisRatio) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            "balanceN",
            "balanceN",
            this.minMaxYAxis,
          );
        }
        if (this.showHedge) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            "hedgeN",
            "hedgeN",
            this.minMaxYAxis,
          );
        }
        if (this.showProgram) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            "programN",
            "programN",
            this.minMaxYAxis,
          );
        }
      } else {
        if (this.showExpo) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            this.fNames["expo"],
            this.fNames["expo"],
            this.minMaxYAxis,
          );
        }
        if (this.showBudget) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            this.fNames["budget"],
            this.fNames["budget"],
            this.minMaxYAxis,
          );
        }
        if (this.showBasisRatio) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            this.fNames["comm"],
            this.fNames["comm"],
            this.minMaxYAxis,
          );
        }
        if (this.showHedge) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            this.fNames["hedge"],
            this.fNames["hedge"],
            this.minMaxYAxis,
          );
        }
        if (this.showProgram) {
          this.minMaxYAxis = this.processData.minMax(
            this.chartData,
            this.fNames["program"],
            this.fNames["program"],
            this.minMaxYAxis,
          );
        }
      }
      this.minMaxYAxis = this.validateMinMaxYAxis(this.minMaxYAxis);
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        this.chartData,
        this.minMaxYAxis,
        this.dateFormat,
        50,
        4,
        false,
        [],
        true,
        true,
        true,
        this.normalizePlanning ? formatPercentSm : numberFormat,
        false,
      );

      let del;
      if (!this.expandFRow) {
        del = 0.03 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      } else {
        del =
          this.minMaxYAxis[1] >= 0 && this.minMaxYAxis[0] < 0
            ? 0.05 * (this.minMaxYAxis[1] - 0)
            : 0.05 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      }
      let valueHedge =
        this.showHedge && this.showProtected
          ? this.fNamesNorm["hedge"]
          : this.showHedge
          ? this.fNamesNorm["fixed"]
          : this.showProtected
          ? this.fNamesNorm["protected"]
          : "";

      const formatP =
        this.chartData.length < 12 ? formatPercentSm : formatPercentXSm;
      this.chartData.forEach(function(d) {
        d.balancePPos = d[_this.fNames["expo"]] > 0 ? del : -del;
        d.balancePPosN = d.cash_flowN > 0 ? del : -del;
        d.valueComm = _this.isAdvanced
          ? d[_this.fNames["expo"]] !== 0
            ? formatP(d[_this.fNames["comm"]] / d[_this.fNames["expo"]])
            : ""
          : formatP(d[_this.fNamesNorm["comm"]]);
        d.valueHedge = _this.isAdvanced
          ? d[_this.fNames["expo"]] !== 0
            ? formatP(d.hedge / d[_this.fNames["expo"]])
            : ""
          : formatP(d[valueHedge]);
        d.hedPPos = d[_this.fNames["expo"]] > 0 ? del : -del;
        d.hedPPosN = d.cash_flowN > 0 ? del : -del;
        d.fixedP =
          d[_this.fNames["expo"]] !== 0
            ? formatP(d.fixed / d[_this.fNames["expo"]])
            : "";
        d.protectedP =
          d[_this.fNames["expo"]] !== 0
            ? formatP(d.protected / d[_this.fNames["expo"]])
            : "";
        d.programPos =
          d[_this.fNames["expo"]] > 0 ? d.program + del : d.program - del;
        d.programPosN = d.cash_flowN > 0 ? d.programN + del : d.programN - del;
        d.programP =
          d[_this.fNames["expo"]] !== 0
            ? formatP(d.program / d[_this.fNames["expo"]])
            : "";
      });

      // const barWidth = 0.7;
      if (this.isFuture) {
        this.D3Chart.backgroundRect(
          this.svgNode,
          this.chartData.map((d) => d.date),
          this.chartData.map((d, i) => i % 2 !== 0),
          "PLAN",
        );
      }
      if (this.showExpo) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.75,
          "PLANCF",
          colorExpo(this.colorTheme),
          colorSymmetryAlpha3(this.colorTheme),
          "date",
          this.normalizePlanning ? "cash_flowN" : this.fNames["expo"],
          true,
          false,
          true,
          2,
          1,
          true,
          "date",
        );
      }
      if (this.showBasisRatio) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.25,
          "PLANB",
          colorHedge(this.colorTheme),
          colorHedgeAlpha2(this.colorTheme),
          "date",
          this.normalizePlanning ? "balanceN" : this.fNames["comm"],
          true,
          false,
          true,
          2,
          1,
          true,
          "date",
          false,
          "",
          0.24,
        );
      }
      if (this.showRequired && !this.expandFRow) {
        if (this.isAdvanced) {
          this.chartData.forEach(function(d) {
            d.hedgeExpand = d.hedge - d.last_m_hedge;
            d.hedgeNExpand = d.hedgeN - d.last_m_hedgeN;
            d.required =
              d.program === 0
                ? 0
                : d.program > 0
                ? Math.max(d.program - d.hedge, 0)
                : -Math.max(d.hedge - d.program, 0);
            d.requiredN =
              d.programN === 0
                ? 0
                : d.programN >= 0
                ? Math.max(d.programN - d.hedgeN, 0)
                : -Math.max(d.hedgeN - d.programN, 0);
            d.requiredPPos = d.hedge > 0 ? d.hedge + del : d.hedge - del;
            d.requiredPPosN = d.hedgeN > 0 ? d.hedgeN + del : d.hedgeN - del;
            d.requiredP =
              d[_this.fNames["expo"]] !== 0 && d.required !== 0
                ? formatPercentSm(d.required / d[_this.fNames["expo"]])
                : "";
          });
        }
        this.D3Chart.twoStackedBars(
          this.svgNode,
          this.chartData,
          0.25,
          "REQH",
          "REQH2",
          "rgba(0,0,0,0)",
          "rgba(0,0,0,0)",
          whiteGray(this.colorTheme),
          whiteGrayAlpha(this.colorTheme),
          "date",
          this.normalizePlanning ? "hedgeN" : this.fNames["hedge"],
          this.normalizePlanning ? "requiredN" : "required",
          true,
          1,
          0.515,
        );
      }
      if (this.showHedge && this.showProtected) {
        if (!this.expandFRow) {
          this.D3Chart.twoStackedBars(
            this.svgNode,
            this.chartData,
            0.25,
            "PLANH",
            "PLANOP",
            colorFwd,
            colorFwdAlpha4(this.colorTheme),
            this.isAdvanced || !this.showFixedHedged
              ? darkFwdF(this.colorTheme)
              : colorNet_2(this.colorTheme),
            this.isAdvanced || !this.showFixedHedged
              ? darkFwdAlpha4(this.colorTheme)
              : colorNetAlpha_2(this.colorTheme),
            "date",
            this.normalizePlanning ? "fixedN" : this.fNames["fixed"],
            this.normalizePlanning ? "protectedN" : this.fNames["protected"],
            true,
            1,
            0.515,
            this.svgNode,
            true,
            "date",
          );
        } else {
          this.D3Chart.bars(
            this.svgNode,
            this.chartData,
            0.25,
            "PLANH",
            colorFwd,
            colorFwdAlpha4(this.colorTheme),
            "date",
            this.normalizePlanning ? "hedgeN" : this.fNames["hedge"],
            true,
            false,
            true,
            2,
            1,
            true,
            "date",
            false,
            "",
            0.51,
          );
        }
      } else if (this.showHedge && !this.showProtected) {
        this.D3Chart.twoStackedBars(
          this.svgNode,
          this.chartData,
          0.25,
          "PLANH",
          "PLANOP",
          colorFwd,
          colorFwdAlpha4(this.colorTheme),
          colorNet_2(this.colorTheme),
          colorNetAlpha_2(this.colorTheme),
          "date",
          this.normalizePlanning ? "fixedN" : this.fNames["fixed"],
          "zeroFld",
          true,
          1,
          0.515,
          this.svgNode,
          true,
          "date",
        );
      } else if (
        !this.showHedge &&
        this.showProtected &&
        this.showFixedHedged
      ) {
        this.D3Chart.twoStackedBars(
          this.svgNode,
          this.chartData,
          0.25,
          "PLANH",
          "PLANOP",
          colorFwd,
          colorFwdAlpha4(this.colorTheme),
          colorNet_2(this.colorTheme),
          colorNetAlpha_2(this.colorTheme),
          "date",
          "zeroFld",
          this.normalizePlanning ? "protectedN" : this.fNames["protected"],
          true,
          1,
          0.515,
          this.svgNode,
          true,
          "date",
        );
      }
      if (this.showBudget) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.9,
          "PLANBudg",
          colorDarkExpo(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.normalizePlanning ? "budgetN" : this.fNames["budget"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
        );
      }
      let normalize = this.normalizePlanning;
      this.chartData.forEach(function(d) {
        if (normalize) {
          d.programN =
            d.programN === 0 &&
            d[_this.fNames["comm"]] + d[_this.fNames["expo"]] === 0
              ? -Infinity
              : d.programN;
        }
        d.program =
          d.program === 0 &&
          d[_this.fNames["comm"]] + d[_this.fNames["expo"]] === 0
            ? -Infinity
            : d.program;
      });
      if (this.showProgram) {
        this.D3Chart.bars(
          this.svgNode,
          this.chartData,
          0.8,
          "PLANProg",
          colorProgram(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          this.normalizePlanning ? "programN" : this.fNames["program"],
          true,
          false,
          false,
          4,
          2,
          true,
          "date",
        );
      }
      if (!this.expandFRow && this.showRequired && this.chartData.length < 25) {
        this.D3Chart.text(
          this.svgNode,
          this.chartData,
          "date",
          this.normalizePlanning ? "requiredPPosN" : "requiredPPos",
          "requiredP",
          "reqTxt",
          "1.1rem",
          0.64 * this.svgNode.x.bandwidth(),
          -5,
          "middle",
          "central",
          0,
          textactive2(this.colorTheme),
        );
      }
      // if (this.showProgram && !this.expandFRow && this.chartData.length < 25) {
      //   this.D3Chart.text(
      //     this.svgNode,
      //     this.chartData,
      //     "date",
      //     this.normalizePlanning ? "programPosN" : "programPos",
      //     "programP",
      //     "progTxt",
      //     "1.1rem",
      //     this.svgNode.x.bandwidth() / 2,
      //     -5,
      //     "middle",
      //     "central",
      //     0,
      //     this.colorTheme
      //       ? colorProgram(this.colorTheme)
      //       : textactive(this.colorTheme),
      //   );
      // }
      if (this.showBasisRatio && this.chartData.length < 18) {
        this.D3Chart.text(
          this.svgNode,
          this.chartData,
          "date",
          this.normalizePlanning ? "balancePPosN" : "balancePPos",
          "valueComm",
          "hTxt",
          "1.1rem",
          0.48 * this.svgNode.x.bandwidth(),
          -4,
          "end",
          "central",
          0,
          textactive(this.colorTheme),
        );
      }
      if (
        (this.showHedge || this.showProtected) &&
        this.chartData.length < 18
      ) {
        this.D3Chart.text(
          this.svgNode,
          this.chartData,
          "date",
          this.normalizePlanning ? "hedPPosN" : "hedPPos",
          "valueHedge",
          "hTxt",
          "1rem",
          0.52 * this.svgNode.x.bandwidth(),
          -4,
          "start",
          "central",
          0,
          textactive(this.colorTheme),
        );
      }
      // x axis BUY SELL label
      let sign = this.chartData[0].sign;
      if (!this.normalizePlanning) {
        if (this.minMaxYAxis[0] < 0) {
          this.svgNode
            .append("g")
            .append("text")
            .attr("y", 6)
            .attr("dy", ".71em")
            .attr("class", "axis-label")
            .attr(
              "transform",
              `translate(${this.svgNode.width - 20}, ${this.svgNode.height -
                2}) rotate(-90)`,
            )
            .style("text-anchor", "start")
            .style("dominant-baseline", "ideographic")
            .style("font-size", "0.9rem")
            .text(sign >= 0 ? "Buy" : "Sell");
        }
        if (this.minMaxYAxis[1] > 0) {
          this.svgNode
            .append("g")
            .append("text")
            .attr("y", 6)
            .attr("dy", ".71em")
            .attr("class", "axis-label")
            .attr(
              "transform",
              `translate(${this.svgNode.width - 20}, +4) rotate(-90)`,
            )
            .style("text-anchor", "end")
            .style("dominant-baseline", "ideographic")
            .style("font-size", "0.9rem")
            .text(sign >= 0 ? "Sell" : "Buy");
        }
      }
      // === hover fx ===
      this.D3HoverPlanning.hoverFct(
        this.svgNode,
        this.chartData,
        "PLAN",
        formatNumber,
        this.currency,
        this.normalizePlanning ? formatPercent : numberFormat,
        this.dateFormat,
        this.currencyFormat,
        this.budgetRate,
        formatPercent,
        this.showTooltip,
        formatM,
        this.averages,
        false,
        this.asset,
        this.isFuture,
        this.colorTheme,
        true,
        this,
        "date",
        false,
        [{}],
        "",
        [],
        false,
        [],
        false,
        true,
      );
    },
    onHoverChart() {
      this.$emit("onTableFormat", this.formatVars.userHovered);
    },
    callWidth() {
      if (this.chartData.length > 0) {
        this.createBarChart();
      }
    },
  },
};
</script>

<style scoped></style>
